import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Alert,
  Snackbar,
  LinearProgress,
  Fade,
  Zoom,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Tooltip,
  CircularProgress,
  Paper
} from '@mui/material';
import {
  Sensors as SensorsIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Bluetooth as BluetoothIcon,
  Wifi as WifiIcon,
  SignalCellular4Bar as SignalCellularIcon,
  BatteryFull as BatteryFullIcon,
  BatteryChargingFull as BatteryChargingFullIcon,
  Settings as SettingsIcon,
  QrCode as QrCodeIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  Pause as PauseIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import ModernLayout from './common/ModernLayout';
import ModernCard from './common/ModernCard';
import ModernButton, { GradientButton, GlassButton } from './common/ModernButton';
import axiosInstance from './axiosConfig';
import { useLanguage } from '../contexts/LanguageContext';

const SensorAdoption = () => {
  const navigate = useNavigate();
  const { translate } = useLanguage();
  const [pendingSensors, setPendingSensors] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [isScanning, setIsScanning] = useState(false);
  const [scanProgress, setScanProgress] = useState(0);
  const [openAdoptionModal, setOpenAdoptionModal] = useState(false);
  const [selectedSensor, setSelectedSensor] = useState(null);
  const [adoptionForm, setAdoptionForm] = useState({
    name: '',
    roomId: '',
    sensorType: 'temperature',
    description: '',
    location: '',
    threshold: '',
    alertEnabled: true
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    fetchProjects();
    fetchPendingSensors();
  }, []);

  useEffect(() => {
    if (selectedProject) {
      fetchRooms(selectedProject);
    }
  }, [selectedProject]);

  const fetchProjects = async () => {
    try {
      const userId = localStorage.getItem('userId');
      const response = await axiosInstance.get(`/api/projects/${userId}`);
      setProjects(response.data);
      if (response.data.length > 0) {
        setSelectedProject(response.data[0].id);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
      showSnackbar(translate('errorFetchingProjects'), 'error');
    }
  };

  const fetchRooms = async (projectId) => {
    try {
      const response = await axiosInstance.get(`/api/rooms/${projectId}`);
      setRooms(response.data);
    } catch (error) {
      console.error('Error fetching rooms:', error);
      showSnackbar(translate('errorFetchingRooms'), 'error');
    }
  };

  const fetchPendingSensors = async () => {
    try {
      const response = await axiosInstance.get('/api/pending-sensors');
      setPendingSensors(response.data);
    } catch (error) {
      console.error('Error fetching pending sensors:', error);
      showSnackbar(translate('errorFetchingSensors'), 'error');
    }
  };

  const startSensorScan = async () => {
    setIsScanning(true);
    setScanProgress(0);
    
    // Simulate scanning progress
    const interval = setInterval(() => {
      setScanProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setIsScanning(false);
          fetchPendingSensors(); // Refresh sensor list
          showSnackbar(translate('scanCompleted'), 'success');
          return 100;
        }
        return prev + 10;
      });
    }, 500);
  };

  const handleAdoptSensor = (sensor) => {
    setSelectedSensor(sensor);
    setAdoptionForm({
      name: '',
      roomId: '',
      sensorType: 'temperature',
      description: '',
      location: '',
      threshold: '',
      alertEnabled: true
    });
    setOpenAdoptionModal(true);
  };

  const handleSaveAdoption = async () => {
    try {
      await axiosInstance.post('/api/register-sensor', {
        sensorId: selectedSensor.id,
        name: adoptionForm.name,
        roomId: adoptionForm.roomId,
        projectId: selectedProject,
        sensorType: adoptionForm.sensorType,
        description: adoptionForm.description,
        location: adoptionForm.location,
        threshold: adoptionForm.threshold,
        alertEnabled: adoptionForm.alertEnabled
      });
      
      showSnackbar(translate('sensorAdoptedSuccessfully'), 'success');
      setOpenAdoptionModal(false);
      fetchPendingSensors();
    } catch (error) {
      console.error('Error adopting sensor:', error);
      showSnackbar(translate('errorAdoptingSensor'), 'error');
    }
  };

  const getSensorIcon = (sensorType) => {
    switch (sensorType) {
      case 'temperature': return <SensorsIcon color="error" />;
      case 'humidity': return <SensorsIcon color="info" />;
      case 'co2': return <SensorsIcon color="warning" />;
      case 'soil_moisture': return <SensorsIcon color="success" />;
      default: return <SensorsIcon />;
    }
  };

  const getSensorStatusColor = (sensor) => {
    if (sensor.batteryLevel < 20) return 'error';
    if (sensor.signalStrength < 50) return 'warning';
    return 'success';
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const getSensorTypeLabel = (sensorType) => {
    switch (sensorType) {
      case 'temperature': return translate('temperatureSensor');
      case 'humidity': return translate('humiditySensor');
      case 'co2': return translate('co2Sensor');
      case 'soil_moisture': return translate('soilMoistureSensor');
      default: return translate('unknownSensor');
    }
  };

  return (
    <ModernLayout title={translate('sensorAdoption')}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
          {translate('sensorAdoption')}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {translate('sensorAdoptionDescription')}
        </Typography>
      </Box>

      {/* Project Selection */}
      <ModernCard
        title={translate('selectProject')}
        subtitle={translate('selectProjectForSensors')}
        sx={{ mb: 3 }}
      >
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {projects.map((project) => (
            <Chip
              key={project.id}
              label={project.name}
              variant={selectedProject === project.id ? 'filled' : 'outlined'}
              color={selectedProject === project.id ? 'primary' : 'default'}
              onClick={() => setSelectedProject(project.id)}
              sx={{ cursor: 'pointer' }}
            />
          ))}
        </Box>
      </ModernCard>

      {/* Scan Controls */}
      <ModernCard
        title={translate('sensorDiscovery')}
        subtitle={translate('scanForNewSensors')}
        sx={{ mb: 3 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <GradientButton
            startIcon={isScanning ? <PauseIcon /> : <PlayIcon />}
            onClick={startSensorScan}
            disabled={isScanning}
            sx={{ minWidth: 150 }}
          >
            {isScanning ? translate('scanning') : translate('startScan')}
          </GradientButton>
          
          <GlassButton
            startIcon={<RefreshIcon />}
            onClick={fetchPendingSensors}
            disabled={isScanning}
          >
            {translate('refresh')}
          </GlassButton>
        </Box>

        {isScanning && (
          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">{translate('scanningProgress')}</Typography>
              <Typography variant="body2">{scanProgress}%</Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={scanProgress}
              sx={{ 
                height: 8, 
                borderRadius: 4,
                backgroundColor: 'rgba(255, 255, 255, 0.3)',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 4,
                  background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)'
                }
              }}
            />
          </Box>
        )}
      </ModernCard>

      {/* Pending Sensors */}
      <ModernCard
        title={translate('pendingSensors')}
        subtitle={`${pendingSensors.length} ${translate('sensorsWaiting')}`}
      >
        {pendingSensors.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <SensorsIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {translate('noPendingSensors')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {translate('startScanToDiscover')}
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {pendingSensors.map((sensor) => (
              <Grid item xs={12} md={6} lg={4} key={sensor.id}>
                <Zoom in={true} style={{ transitionDelay: `${sensor.id % 3 * 100}ms` }}>
                  <Card sx={{
                    background: 'rgba(255, 255, 255, 0.95)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: 3,
                    boxShadow: 3,
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 6,
                    }
                  }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Avatar sx={{
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          mr: 2
                        }}>
                          {getSensorIcon(sensor.type || 'temperature')}
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {sensor.identifier}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {getSensorTypeLabel(sensor.type || 'temperature')}
                          </Typography>
                        </Box>
                        <Chip
                          label={translate('new')}
                          color="primary"
                          size="small"
                          variant="outlined"
                        />
                      </Box>

                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {translate('sensorDetails')}
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <SignalCellularIcon fontSize="small" color="primary" />
                            <Typography variant="caption">
                              {translate('signalStrength')}: {sensor.signalStrength || 'N/A'}%
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <BatteryFullIcon fontSize="small" color="success" />
                            <Typography variant="caption">
                              {translate('batteryLevel')}: {sensor.batteryLevel || 'N/A'}%
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <WifiIcon fontSize="small" color="info" />
                            <Typography variant="caption">
                              {translate('lastSeen')}: {sensor.lastSeen ? new Date(sensor.lastSeen).toLocaleString() : translate('never')}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>

                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <GradientButton
                          fullWidth
                          startIcon={<AddIcon />}
                          onClick={() => handleAdoptSensor(sensor)}
                        >
                          {translate('adopt')}
                        </GradientButton>
                        <Tooltip title={translate('viewDetails')}>
                          <IconButton
                            size="small"
                            onClick={() => {/* View sensor details */}}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </CardContent>
                  </Card>
                </Zoom>
              </Grid>
            ))}
          </Grid>
        )}
      </ModernCard>

      {/* Sensor Adoption Modal */}
      <Dialog
        open={openAdoptionModal}
        onClose={() => setOpenAdoptionModal(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            background: 'white',
            borderRadius: 3,
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            border: '1px solid rgba(0, 0, 0, 0.1)',
          },
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          fontWeight: 700,
          fontSize: '1.3rem',
        }}>
          {translate('adoptSensor')} - {selectedSensor?.identifier}
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  label={translate('sensorName')}
                  fullWidth
                  value={adoptionForm.name}
                  onChange={(e) => setAdoptionForm({...adoptionForm, name: e.target.value})}
                  placeholder={translate('enterSensorName')}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.4)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#667eea',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: 'rgba(0, 0, 0, 0.7)',
                    },
                    '& .MuiInputBase-input': {
                      color: 'rgba(0, 0, 0, 0.87)',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  label={translate('sensorType')}
                  fullWidth
                  value={adoptionForm.sensorType}
                  onChange={(e) => setAdoptionForm({...adoptionForm, sensorType: e.target.value})}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.4)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#667eea',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: 'rgba(0, 0, 0, 0.7)',
                    },
                    '& .MuiSelect-select': {
                      color: 'rgba(0, 0, 0, 0.87)',
                    },
                  }}
                >
                  <option value="temperature">{translate('temperatureSensor')}</option>
                  <option value="humidity">{translate('humiditySensor')}</option>
                  <option value="co2">{translate('co2Sensor')}</option>
                  <option value="soil_moisture">{translate('soilMoistureSensor')}</option>
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  label={translate('assignToRoom')}
                  fullWidth
                  value={adoptionForm.roomId}
                  onChange={(e) => setAdoptionForm({...adoptionForm, roomId: e.target.value})}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.4)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#667eea',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: 'rgba(0, 0, 0, 0.7)',
                    },
                    '& .MuiSelect-select': {
                      color: 'rgba(0, 0, 0, 0.87)',
                    },
                  }}
                >
                  {rooms.map((room) => (
                    <option key={room.id} value={room.id}>
                      {room.name}
                    </option>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={translate('location')}
                  fullWidth
                  value={adoptionForm.location}
                  onChange={(e) => setAdoptionForm({...adoptionForm, location: e.target.value})}
                  placeholder={translate('enterLocation')}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.4)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#667eea',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: 'rgba(0, 0, 0, 0.7)',
                    },
                    '& .MuiInputBase-input': {
                      color: 'rgba(0, 0, 0, 0.87)',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label={translate('description')}
                  fullWidth
                  multiline
                  rows={3}
                  value={adoptionForm.description}
                  onChange={(e) => setAdoptionForm({...adoptionForm, description: e.target.value})}
                  placeholder={translate('enterDescription')}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.4)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#667eea',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: 'rgba(0, 0, 0, 0.7)',
                    },
                    '& .MuiInputBase-input': {
                      color: 'rgba(0, 0, 0, 0.87)',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={translate('threshold')}
                  fullWidth
                  type="number"
                  value={adoptionForm.threshold}
                  onChange={(e) => setAdoptionForm({...adoptionForm, threshold: e.target.value})}
                  placeholder={translate('enterThreshold')}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.4)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#667eea',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: 'rgba(0, 0, 0, 0.7)',
                    },
                    '& .MuiInputBase-input': {
                      color: 'rgba(0, 0, 0, 0.87)',
                    },
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button 
            onClick={() => setOpenAdoptionModal(false)}
            sx={{ 
              textTransform: 'none',
              color: 'rgba(0, 0, 0, 0.7)',
              '&:hover': {
                background: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            {translate('cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveAdoption}
            disabled={!adoptionForm.name || !adoptionForm.roomId}
            sx={{
              textTransform: 'none',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
              },
              '&:disabled': {
                background: 'rgba(0, 0, 0, 0.12)',
                color: 'rgba(0, 0, 0, 0.38)',
              },
            }}
          >
            {translate('adoptSensor')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({...snackbar, open: false})}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({...snackbar, open: false})}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </ModernLayout>
  );
};

export default SensorAdoption; 